import * as React from 'react';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteChangeReason, createFilterOptions } from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { DropdownOptions, IFormController } from '../types';
import { CircularProgress, InputLabel, Popper } from '@mui/material';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';

interface MyMultiSelectOptions extends IFormController {
  customLabel?: boolean;
  id?: string;
  filtersDesign?: boolean;
  selectedItem?: any;
  required?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  selectAllLabel?: string
}

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

export default function MultiSelectAll(props: MyMultiSelectOptions) {
  const {
    label, customLabel, options, required, selectedItem, filtersDesign, isLoading, selectAllLabel = 'Όλες οι επιλογές', ...rest 
  } = props;

  const { t } = useTranslation();

  const emptyMsg = isLoading ? 
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <CircularProgress size={20} />
    </Box>
    : t('Μη διαθέσιμες επιλογές');

  const reference = React.useRef<HTMLDivElement>(null);
  const PopperMy = React.useCallback((props: any) => {
    return <Popper {...props}
      style={{ width: reference?.current?.clientWidth, position: 'absolute', backgroundColor: 'inherit' }}
      placement='bottom-start'
    />;
  }, []);
  
  const [selectedOptions, setSelectedOptions] = React.useState<any>([]);

  const allSelected = options?.length === selectedOptions.length;

  const initialValueInOptions = (options: DropdownOptions[] | any, field: DropdownOptions) =>
    options?.some((x: any) => x.value == field.value);
  
  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedOptions(options);
      props?.formik?.setFieldValue(props.name, options);
    } else {
      setSelectedOptions([]);
      props?.formik?.setFieldValue(props.name, undefined);

    }
  };

  const handleChange = (values: DropdownOptions[], reason: AutocompleteChangeReason) => {        
    if (reason === 'selectOption' || reason === 'removeOption') {

      if (values.find((option: DropdownOptions) => option.value === 'select-all')) {        
        handleSelectAll(!allSelected);
      } else {
        setSelectedOptions(values);
        props?.formik?.setFieldValue(props.name, values);
      }
      
    } else if (reason === 'clear') {
      setSelectedOptions([]);
      props?.formik?.setFieldValue(props.name, values);
    }

    selectedItem && selectedItem(values);
    props?.formik?.setTouched({ [props.name]: true });

  };

  const filter = createFilterOptions();

  React.useEffect(() => {
    if (props?.formik?.values[props.name]
      && props?.formik?.values[props.name].length > 0) {
      const values: DropdownOptions[] =
        props.formik?.values?.[props.name].map((field: DropdownOptions) => {
          if (initialValueInOptions(options, field)) {
            return options?.find((x: any) => x.value == field.value);
          }
        }).filter((x: any) => x);

      handleChange(values, 'selectOption');
    } else if (props?.formik?.initialValues[props.name]
      && props?.formik?.initialValues[props.name].length > 0) {
      const values: DropdownOptions[] =
        props.formik?.initialValues?.[props.name].map((field: DropdownOptions) => {
          if (initialValueInOptions(options, field)) {
            return options?.find((x: any) => x.value == field.value);
          }
        }).filter((x: any) => x);

      handleChange(values, 'selectOption');
    }
  }, [options]);

  React.useEffect(() => {
    if (props?.formik?.initialValues[props.name]
      && props?.formik?.initialValues[props.name].length > 0) {
      const values: DropdownOptions[] =
        props.formik?.initialValues?.[props.name].map((field: DropdownOptions) => {
          if (initialValueInOptions(options, field)) {
            return options?.find((x: any) => x.value == field.value);
          }
        }).filter((x: any) => x);

      handleChange(values, 'selectOption');
    }
  }, [props?.formik?.initialValues[props.name]]);

  return (
    <>
      {
        customLabel
          ? <InputLabel
            htmlFor={props.name}
            disabled={props.disabled}
            required={required}
            sx={{
              my: customLabel
                ? 2
                : 0,
              textAlign: 'left',
              textStyle: 'none',
            }}
          >
            {label}
          </InputLabel>
          : null
      }
      <div ref={reference} style={{ position: 'relative' }}>
        <Autocomplete
          sx={{
            ...(filtersDesign && {
              '& .MuiOutlinedInput-root': {
                '& > fieldset': {
                  borderRadius: '24px'
                }
              }
            })
          }}
          PopperComponent={PopperMy}
          disablePortal
          multiple
          noOptionsText={emptyMsg}
          options={options || []}
          value={selectedOptions}
          {...rest}
          limitTags={3}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            return [{ key: selectAllLabel, value: 'select-all' }, ...filtered];
          }}
          disableCloseOnSelect
          getOptionLabel={(option) => option.key}
          onChange={(_, newValue, reason) => {
            handleChange(newValue, reason);
          }} 
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={
                  option.value === 'select-all'
                    ? allSelected
                    : selected
                }
              />
              {option.key}
            </li>
          )}
          renderInput={(params) => (
            <TextField 
              {...params}
              placeholder={props.placeholder || ''}
              label={customLabel
                ? null
                : label} 
            />
          )}
        />
      </div>
    </>
  );
}